import { useState } from 'react';
import { useQuery } from '@tanstack/react-query';
import { useTranslation } from 'react-i18next';
import type { ZipaTransferInput, ZipaTransferPreview, ZipaTransferReceipt } from '@pokerroom/types';
import { api } from '../../../lib/api';
import { useFinancialCapabilities, useFinancialOperation } from './financialShared';
import { BUTTON, INPUT, PANEL, Field } from './walletShared';

export function ZipaTransferPanel({ memberId }: { memberId: string }) {
  const cap = useFinancialCapabilities(memberId);
  if (!cap.venue || !cap.can('member:zipa_transfer')) return null;
  return <TransferForm key={cap.identity} memberId={memberId} cap={cap} />;
}
function TransferForm({
  memberId,
  cap,
}: {
  memberId: string;
  cap: ReturnType<typeof useFinancialCapabilities>;
}) {
  const { t } = useTranslation('member');
  const op = useFinancialOperation<ZipaTransferInput, ZipaTransferPreview, ZipaTransferReceipt>(
    memberId,
    'zipa-transfers',
    cap.identity,
  );
  const [input, setInput] = useState<ZipaTransferInput>(
    op.pending ?? { sourceVenueId: cap.venue!.id, destinationVenueId: '', amount: '', reason: '' },
  );
  const ready = input.reversesTransferId
    ? cap.data?.transferCorrectionAvailable === true
    : cap.data?.transferPostingAvailable === true;
  const history = useQuery({
    queryKey: ['member-transfers', memberId, cap.identity, cap.revision],
    queryFn: () => api.get<ZipaTransferReceipt[]>(`/members/${memberId}/zipa-transfers`),
    retry: false,
  });
  const change = (next: ZipaTransferInput) => {
    setInput(next);
    op.reset();
  };
  const sides = (row: ZipaTransferPreview | ZipaTransferReceipt) => (
    <div className="grid gap-3 sm:grid-cols-2">
      {(
        [
          ['source', row.source],
          ['destination', row.destination],
        ] as const
      ).map(([key, side]) => (
        <div key={key} className="rounded border border-base-300 p-3">
          <p className="font-medium">
            {t(`financial.${key}`)}: {side.venueName}
          </p>
          <p>
            {side.balanceBefore} → {side.balanceAfter} ZIPA
          </p>
          <p className="text-sm">
            {t('financial.accountingDate')}: {side.sessionDate}
          </p>
        </div>
      ))}
      <p className="text-sm sm:col-span-2">
        {t('financial.zipaDate')}: {row.zipaBusinessDate} · {t('financial.noncash')}
      </p>
    </div>
  );
  return (
    <section className={PANEL} aria-label={t('financial.transferTitle')}>
      <h3 className="font-semibold">{t('financial.transferTitle')}</h3>
      {!ready && <p role="status">{t('financial.unavailable')}</p>}
      <fieldset disabled={op.locked || !ready} className="grid gap-3 sm:grid-cols-2">
        {(['sourceVenueId', 'destinationVenueId'] as const).map((key, index) => (
          <Field key={key} label={t(index ? 'financial.destination' : 'financial.source')}>
            <select
              aria-label={t(index ? 'financial.destination' : 'financial.source')}
              className={INPUT}
              value={input[key]}
              onChange={(e) =>
                change({ ...input, [key]: e.target.value, reversesTransferId: undefined })
              }
              disabled={!!input.reversesTransferId}
            >
              <option value="">{t('financial.selectVenue')}</option>
              {cap.data?.transferVenues.map((v) => (
                <option key={v.id} value={v.id}>
                  {v.name}
                </option>
              ))}
            </select>
          </Field>
        ))}
        <Field label={t('financial.amount')}>
          <input
            aria-label={t('financial.amount')}
            className={INPUT}
            inputMode="decimal"
            value={input.amount}
            disabled={!!input.reversesTransferId}
            onChange={(e) => change({ ...input, amount: e.target.value })}
          />
        </Field>
        <Field label={t('financial.reason')}>
          <input
            aria-label={t('financial.reason')}
            className={INPUT}
            maxLength={255}
            value={input.reason}
            onChange={(e) => change({ ...input, reason: e.target.value })}
          />
        </Field>
        <button
          className={BUTTON}
          type="button"
          disabled={
            !input.destinationVenueId ||
            !input.reason.trim() ||
            !input.amount ||
            input.sourceVenueId === input.destinationVenueId ||
            ![input.sourceVenueId, input.destinationVenueId].includes(cap.venue!.id)
          }
          onClick={() => void op.load(input)}
        >
          {t('financial.preview')}
        </button>
      </fieldset>
      {input.reversesTransferId && (
        <div className="break-all">
          <p>
            {t('financial.inverseOf')}: {input.reversesTransferId}
          </p>
          <button
            className={BUTTON}
            disabled={op.locked}
            onClick={() =>
              change({
                sourceVenueId: cap.venue!.id,
                destinationVenueId: '',
                amount: '',
                reason: '',
              })
            }
          >
            {t('financial.newTransfer')}
          </button>
        </div>
      )}
      {op.preview && (
        <div className="space-y-3">
          {sides(op.preview)}
          <button
            className={BUTTON}
            disabled={op.locked || !ready}
            onClick={() =>
              void op.submit({
                ...input,
                amount: op.preview!.amount,
                quoteHash: op.preview!.quoteHash,
                operationKey: crypto.randomUUID(),
              })
            }
          >
            {t('financial.confirmTransfer')}
          </button>
        </div>
      )}
      {op.error && <p role="alert">{t(op.error)}</p>}
      {op.pending && (
        <button
          className={BUTTON}
          disabled={
            op.busy || !(cap.data?.transferRecoveryAvailable ?? cap.data?.transferPostingAvailable)
          }
          onClick={() => void op.submit(op.pending!)}
        >
          {t('financial.retry')}
        </button>
      )}
      {op.receipt && (
        <div role="status" className="space-y-3">
          <p className="break-all">
            {t('financial.completed')}: {op.receipt.id}
          </p>
          {sides(op.receipt)}
        </div>
      )}
      <h4 className="font-medium">{t('financial.transferHistory')}</h4>
      {history.isError && <p role="alert">{t('financial.historyFailed')}</p>}
      {history.data?.map((row) => (
        <details key={row.id} className="rounded border border-base-300 p-3">
          <summary>
            {row.source.venueName} → {row.destination.venueName} · {row.amount} ZIPA
          </summary>
          <div className="mt-3 space-y-2">
            {sides(row)}
            <p>{row.reason}</p>
            <p className="break-all">
              {t('financial.receipt')}: {row.id}
            </p>
            <p className="break-all">
              {t('financial.sourceRefs')}: {row.sourceLogId} / {row.destinationLogId}
            </p>
            <p className="break-all">
              {t('financial.accountingRefs')}: {row.sourcePostingId} / {row.destinationPostingId}
            </p>
            {row.reversesTransferId && (
              <p>
                {t('financial.inverseOf')}: {row.reversesTransferId}
              </p>
            )}
            {row.correctionId && (
              <p>
                {t('financial.correctedBy')}: {row.correctionId}
              </p>
            )}
            {!row.correctionId &&
              !row.reversesTransferId &&
              (cap.data?.transferCorrectionAvailable ?? ready) && (
                <button
                  className={BUTTON}
                  disabled={op.locked}
                  onClick={() =>
                    change({
                      sourceVenueId: row.destination.venueId,
                      destinationVenueId: row.source.venueId,
                      amount: row.amount,
                      reason: '',
                      reversesTransferId: row.id,
                    })
                  }
                >
                  {t('financial.prepareInverse')}
                </button>
              )}
          </div>
        </details>
      ))}
    </section>
  );
}
